import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import {
  HandWaving, Package, ShoppingCart, Globe, Truck, Gear, ArrowsClockwise,
  CaretLeft, CaretRight, EyeSlash
} from '@phosphor-icons/react';

export const TOUR_STORAGE_KEY = 'magere_admin_tour_hidden';

const STEPS = [
  {
    icon: HandWaving, color: '#c2185b', title: 'Bienvenue dans votre back office !',
    points: [
      'Le menu latéral est organisé par sections colorées : Catalogue, Commerce, Logistique, Contenu du site, Configuration.',
      'La barre du haut affiche vos chiffres en direct : CA du jour, CA du mois, panier moyen, clients, produits.',
      'Le bouton « Voir le site » ouvre votre boutique dans un nouvel onglet. « Activer notifs » vous alerte sur cet appareil à chaque nouvelle commande.',
    ],
  },
  {
    icon: Package, color: '#f59e0b', title: 'Catalogue — vos produits',
    points: [
      'Produits : prix, photos, promotions (prix barré), prix professionnels B2B, poids.',
      'Catégories : image, ordre d\'affichage, visibilité (icône œil pour masquer/afficher sur le site).',
      'Stock : suivez et ajustez les quantités disponibles.',
    ],
  },
  {
    icon: ShoppingCart, color: '#10b981', title: 'Commandes — le cœur du workflow',
    points: [
      'Onglets : Payées (à traiter), Intentions (paiement non finalisé — bouton « Vérifier Stripe » pour contrôler), Tests (exclues des stats).',
      'Cycle d\'une commande : Reçue → Payée (emails automatiques client + admin) → En préparation → En livraison → Livrée. Chaque changement de statut envoie un email au client.',
      'Sur chaque commande : facture PDF, bon de livraison PDF (avec poids), marquage TEST, et « Demander un avis » Google après livraison.',
    ],
  },
  {
    icon: Globe, color: '#ec4899', title: 'Contenu du site — textes, pages et images',
    points: [
      'Pages du site : « Le magasin » et « Nous contacter » ont un éditeur visuel dédié (présentation, accès routier, horaires, section professionnels, photos). CGV et pages libres s\'éditent en texte riche.',
      'Blog / Conseils : vos articles et recettes. Popups & bannières : annonces sur le site.',
      'Médias : bibliothèque d\'images (logo, catégories, produits).',
    ],
  },
  {
    icon: Truck, color: '#8b5cf6', title: 'Règles de livraison',
    points: [
      'Zones de livraison : codes postaux + villes desservies, jours de livraison propres à chaque zone (ex : Zone 1 mar/jeu/sam, Zone 2 mer/ven).',
      'Un client hors zone ne verra que le retrait en boutique au moment de commander.',
      'Retrait en boutique : créneaux horaires, retrait le jour même possible (heure limite et délai de préparation configurables).',
    ],
  },
  {
    icon: Gear, color: '#64748b', title: 'Configuration',
    points: [
      'Entreprise : LA source des coordonnées affichées sur le site (page contact, pied de page, signatures email) + horaires, TVA, lien avis Google.',
      'Identité visuelle : logo, couleurs, réseaux sociaux. Notifications & SMTP : emails automatiques + historique des envois avec bouton renvoyer.',
      'Méthodes de paiement : clés Stripe / PayPal (test ou live). Documents légaux : mentions, confidentialité.',
    ],
  },
  {
    icon: ArrowsClockwise, color: '#0ea5e9', title: 'Le workflow type, de A à Z',
    points: [
      '1. Le client commande et paie → il reçoit un email de confirmation, vous êtes notifié (email + push).',
      '2. Vous préparez : imprimez le bon de livraison PDF, passez la commande « En préparation » puis « En livraison » (le client est informé à chaque étape).',
      '3. Une fois livrée, marquez « Livrée » puis envoyez la « Demande d\'avis » Google en un clic.',
      'Ce guide reste accessible à tout moment via le bouton « ? Guide » en haut de l\'écran.',
    ],
  },
];

export const AdminGuideTour = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0);
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  const close = () => { onOpenChange(false); setStep(0); };
  const hideForever = () => { localStorage.setItem(TOUR_STORAGE_KEY, '1'); close(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) close(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="admin-guide-tour">
        <div className="p-6 pb-4" style={{ backgroundColor: s.color + '10' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '22' }}>
              <Icon size={26} weight="fill" style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: s.color }}>Guide de l'admin — étape {step + 1}/{STEPS.length}</p>
              <h2 className="text-lg font-bold text-slate-900 leading-tight">{s.title}</h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-2.5 min-h-[180px]">
          {s.points.map((p, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full mt-[7px] flex-shrink-0" style={{ backgroundColor: s.color }} />
              <p className="text-[13.5px] text-slate-600 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 pb-3">
          {STEPS.map((_, i) => (
            <button key={i} onClick={() => setStep(i)}
              className="w-2 h-2 rounded-full transition-all"
              style={{ backgroundColor: i === step ? s.color : '#e2e8f0', transform: i === step ? 'scale(1.3)' : 'scale(1)' }}
              data-testid={`tour-dot-${i}`} />
          ))}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/60">
          <button onClick={hideForever} className="flex items-center gap-1.5 text-[12px] text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-hide-forever">
            <EyeSlash size={14} /> Ne plus afficher
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} className="h-8 gap-1" data-testid="tour-prev">
                <CaretLeft size={14} /> Précédent
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={close} className="h-8" style={{ backgroundColor: s.color }} data-testid="tour-finish">
                Terminer
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep(step + 1)} className="h-8 gap-1" style={{ backgroundColor: s.color }} data-testid="tour-next">
                Suivant <CaretRight size={14} />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
